import React, { useEffect, useCallback, useState } from "react"
import "@xyflow/react/dist/style.css"
import Autocomplete from "@mui/material/Autocomplete"
import TextField from "@mui/material/TextField"
import { DatePicker } from "@mui/x-date-pickers"
import Parse from "parse"
import { parseLimitRequest } from "../../../utils"
import FlowProductionItem from "./FlowProductionItem"
import PropertiesPanel from "./PropertiesPanel"
import PseProperties from "./PseProperties"
import { toMidnightTimestamp } from "../../../utils/dateUtils"
import { BOLD_STYLE } from "../FlowPage"
import Labels from "../Labels"

export default function FlowPSE() {
    // const dateStr = dayTimestamp ? new Date(dayTimestamp).toISOString().split("T")[0] : null
    const [recipes, setRecipes] = useState([])
    const [selectedRecipe, setSelectedRecipe] = useState(null)
    const [selectedPseId, setSelectedPseId] = useState(null)
    const [selectedPseData, setSelectedPseData] = useState(null)
    const [open, setOpen] = useState(false)
    const [date, setDate] = useState(toMidnightTimestamp(new Date()))

    useEffect(() => {
        const load = async () => {
            const productionItems = await new Parse.Query("ProductionItem")
                .equalTo("productionDate", date)
                .include("recipe")
                .select("recipe.name", "recipe.uniqueCode", "productionDate")
                .limit(parseLimitRequest)
                .find()
            const items = new Set(productionItems)

            //---- removal of ProductionItems with only deleted PSEs ----//
            const promises = productionItems.map(async item => {
                const pses = await new Parse.Query("ProductionStepExecutions")
                    .equalTo("productionItems", item)
                    .notEqualTo("deleted", true)
                    .select("objectId")
                    .find()
                if (pses.length === 0) {
                    items.delete(item)
                }
            })
            await Promise.all(promises)

            //---- recipes retrieval ----//
            const recipes = []
            items.forEach(item => {
                const recipe = item.get("recipe")
                recipes.push({
                    id: recipe.id,
                    uniqueCode: recipe.get("uniqueCode"),
                    name: recipe.get("name"),
                    item
                })
            })

            // Remove duplicates
            const recipesMap = new Map()
            recipes.forEach(recipe => {
                recipesMap.set(recipe.id, recipe)
            })
            const uniqueRecipes = Array.from(recipesMap.values())

            if (uniqueRecipes.length > 0) {
                setSelectedRecipe(uniqueRecipes[0])
            } else {
                setSelectedRecipe(null)
            }
            setRecipes(uniqueRecipes)
        }
        if (date) {
            load()
        }
    }, [date])

    const onPseSelected = useCallback(async (pseId) => {
        setSelectedPseId(pseId)

        // Fetch PSE data to get recipe uniqueCode and PSE name
        try {
            const pse = await new Parse.Query("ProductionStepExecutions")
                .equalTo("objectId", pseId)
                .select("uniqueCode", "productionStepSnapshot.name", "mergedProductionStepExecutions")
                .first({ json: true })

            if (pse) {
                setSelectedPseData({
                    uniqueCode: pse.uniqueCode || "",
                    pseName: pse.productionStepSnapshot?.name || "",
                    mergedPseIds: pse.mergedProductionStepExecutions || []
                })
            }
        } catch (error) {
            console.error("Error fetching PSE data:", error)
        }

        setOpen(true)
    }, [])

    const onDateSelected = useCallback(selectedDate => {
        const midnightDate = toMidnightTimestamp(selectedDate.toDate())
        setDate(midnightDate)
    }, [])

    return (
        <div className="flexColumn stretch flex1">
            <div className="flexColumn spaceBetween" style={{ padding: 10 }}>
                <div className="flexRow center" style={{ paddingBottom: 10, width: "100%", gap: 10 }}>
                    <DatePicker
                        inputFormat="DD/MM/YYYY"
                        variant="inline"
                        inputVariant="outlined"
                        value={date}
                        onChange={onDateSelected}
                        renderInput={(params) => <TextField {...params} />}
                    />
                    <div style={{ flex: 1 }}>
                        <Autocomplete
                            options={recipes}
                            value={selectedRecipe}
                            onChange={(_, recipe) => setSelectedRecipe(recipe)}
                            getOptionLabel={recipe => `${recipe.uniqueCode} - ${recipe.name}`}
                            filterOptions={(options, { inputValue }) => {
                                const lower = inputValue.toLowerCase()
                                return options.filter(r =>
                                    r.uniqueCode?.toLowerCase().includes(lower) ||
                                    r.name?.toLowerCase().includes(lower)
                                )
                            }}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                            renderOption={(props, recipe) => (
                                <li {...props} key={recipe.id}>
                                    <span style={BOLD_STYLE}>{recipe.uniqueCode}</span>&nbsp;- {recipe.name}
                                </li>
                            )}
                            renderInput={(params) => <TextField {...params} size="medium" style={{ minWidth: 300, width: "100%" }} />}
                            disableClearable
                        />
                    </div>
                </div>
                <Labels />
            </div>
            <div className="flexColumn stretch flex1" style={{ marginLeft: 10 }}>
                {selectedRecipe && <FlowProductionItem recipe={selectedRecipe} productionItem={selectedRecipe?.item} onPseSelected={onPseSelected} />}
                <PropertiesPanel
                    open={open}
                    onClose={() => setOpen(false)}
                    recipeUniqueCode={selectedPseData?.uniqueCode || `${selectedPseData?.mergedPseIds?.length || 0} occurrence(s)` || "Nom de l'étape"}
                    pseName={selectedPseData?.pseName}
                >
                    <PseProperties pseId={selectedPseId} />
                </PropertiesPanel>
            </div>
        </div>
    )
}

